import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Grid, Stack, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  TablePagination, Divider, MenuItem, CircularProgress, Avatar, Tooltip, Alert
} from "@mui/material";
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Close as CloseIcon, LocalGasStation as FuelIcon,
  PhotoCamera as PhotoIcon, Speed as SpeedIcon
} from "@mui/icons-material";
import { db, storage } from "../../firebase"; 
import { 
  collection, addDoc, getDocs, query, orderBy, doc, updateDoc, deleteDoc, Timestamp, limit, where 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Fuellogs() {
  const [logs, setLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [lastReading, setLastReading] = useState(0); // Store previous reading

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState({
    vehicleId: "", fuelType: "DIESEL", qty: "", rate: "",
    amount: "", date: new Date().toISOString().split('T')[0],
    personName: "", openingReading: "", closingReading: "", 
    average: 0, photo: ""
  });

  useEffect(() => {
    fetchVehicles();
    fetchFuelLogs();
  }, []);

  // 1. Fetch last closing reading when vehicle changes
  useEffect(() => {
    if (form.vehicleId && !editId) {
      getLastReading(form.vehicleId);
    }
  }, [form.vehicleId]);

  // 2. Auto-calculate amount and average
  useEffect(() => {
    const totalAmount = parseFloat(form.qty || 0) * parseFloat(form.rate || 0);
    
    // Average Logic: (Closing - Opening) / Qty
    const distance = parseFloat(form.closingReading || 0) - parseFloat(form.openingReading || 0);
    const avg = distance > 0 && parseFloat(form.qty) > 0 ? (distance / parseFloat(form.qty)).toFixed(2) : 0;

    setForm(prev => ({ 
      ...prev, 
      amount: totalAmount.toFixed(2),
      average: avg
    }));
  }, [form.qty, form.rate, form.openingReading, form.closingReading]);

  const getLastReading = async (vId) => {
    const q = query(
      collection(db, "fuel_entries"), 
      where("vehicleId", "==", vId), 
      orderBy("createdAt", "desc"), 
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const lastVal = snap.docs[0].data().closingReading;
      setLastReading(lastVal);
      setForm(prev => ({ ...prev, openingReading: lastVal }));
    } else {
      setLastReading(0);
    }
  };

  const fetchVehicles = async () => {
    const snap = await getDocs(collection(db, "vehicles"));
    setVehicles(snap.docs.map(doc => ({ id: doc.id, number: doc.data().vehicalNo })));
  };

  const fetchFuelLogs = async () => {
    setLoading(true);
    const q = query(collection(db, "fuel_entries"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    setLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const sRef = ref(storage, `fuel_receipts/${Date.now()}_${file.name}`);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      setForm({ ...form, photo: url });
    } catch (err) { Swal.fire("Error", "Upload failed", "error"); }
  };

  const handleSubmit = async () => {
    if (!form.vehicleId || !form.qty || !form.closingReading) {
      return Swal.fire("Required", "Please fill Vehicle, Qty, and Closing Reading", "warning");
    }
    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "fuel_entries", editId), { ...form, updatedAt: Timestamp.now() });
      } else {
        await addDoc(collection(db, "fuel_entries"), { ...form, createdAt: Timestamp.now() });
      }
      setOpen(false);
      resetForm();
      fetchFuelLogs();
      Swal.fire("Success", "Fuel log saved", "success");
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setLoading(false);
  };

  const resetForm = () => {
    setEditId(null);
    setLastReading(0);
    setForm({
      vehicleId: "", fuelType: "DIESEL", qty: "", rate: "",
      amount: "", date: new Date().toISOString().split('T')[0],
      personName: "", openingReading: "", closingReading: "", 
      average: 0, photo: ""
    });
  };

  const filtered = logs.filter(l => l.personName.toLowerCase().includes(search.toLowerCase()));

  return (
    <Box p={1} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight="bold">Fuel Consumption Logs</Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: PRIMARY }} onClick={() => { resetForm(); setOpen(true); }}>
          Add Fuel Log
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ borderRadius: 2 }}>
        <Box p={2}>
          <TextField fullWidth size="small" placeholder="Search by person name..." onChange={(e) => setSearch(e.target.value)} />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Sr.</b></TableCell>
                <TableCell><b>Vehicle</b></TableCell>
                <TableCell><b>Reading (In/Out)</b></TableCell>
                <TableCell><b>Qty / Rate</b></TableCell>
                <TableCell><b>Average</b></TableCell>
                <TableCell><b>Total</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="600">{vehicles.find(v => v.id === row.vehicleId)?.number || "N/A"}</Typography>
                    <Typography variant="caption">{row.date}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">Open: {row.openingReading}</Typography>
                    <Typography variant="caption" display="block">Close: {row.closingReading}</Typography>
                  </TableCell>
                  <TableCell>{row.qty}L @ ₹{row.rate}</TableCell>
                  <TableCell>
                    <Typography color="secondary" fontWeight="bold">{row.average} km/L</Typography>
                  </TableCell>
                  <TableCell><b>₹{row.amount}</b></TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => { setEditId(row.id); setForm(row); setOpen(true); }} color="primary"><EditIcon fontSize="small"/></IconButton>
                    <IconButton size="small" onClick={async () => {
                        const conf = await Swal.fire({ title: 'Delete?', showCancelButton: true });
                        if(conf.isConfirmed) { await deleteDoc(doc(db, "fuel_entries", row.id)); fetchFuelLogs(); }
                    }} color="error"><DeleteIcon fontSize="small"/></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* FORM DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'bold' }}>Fuel Entry & Efficiency</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} mt={1}>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField select label="Select Vehicle" fullWidth size="small" value={form.vehicleId} onChange={(e) => setForm({...form, vehicleId: e.target.value})}>
                {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{v.number}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={6}><TextField label="Opening Meter" type="number" fullWidth size="small" value={form.openingReading} onChange={(e) => setForm({...form, openingReading: e.target.value})} /></Grid>
            <Grid className="col-sm-6 mb-3" item xs={6}><TextField label="Closing Meter" type="number" fullWidth size="small" value={form.closingReading} onChange={(e) => setForm({...form, closingReading: e.target.value})} /></Grid>
            
            <Grid className="col-sm-6 mb-3" item xs={4}><TextField label="Quantity (Ltr)" type="number" fullWidth size="small" value={form.qty} onChange={(e) => setForm({...form, qty: e.target.value})} /></Grid>
            <Grid className="col-sm-6 mb-3" item xs={4}><TextField label="Rate (₹)" type="number" fullWidth size="small" value={form.rate} onChange={(e) => setForm({...form, rate: e.target.value})} /></Grid>
            <Grid className="col-sm-6 mb-3" item xs={4}><TextField label="Amount" fullWidth size="small" disabled value={form.amount} /></Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <Alert icon={<SpeedIcon />} severity="info" sx={{ fontWeight: 'bold' }}>
                Estimated Average: {form.average} km/L
              </Alert>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}><TextField label="Driver Name" fullWidth size="small" value={form.personName} onChange={(e) => setForm({...form, personName: e.target.value})} /></Grid>
            
            {/* <Grid className="col-sm-6 mb-3" item xs={12} textAlign="center">
              <Button component="label" variant="outlined" startIcon={<PhotoIcon />}>
                Upload Receipt
                <input type="file" hidden onChange={handleFileUpload} />
              </Button>
            </Grid> */}
          </Grid>
          <Button variant="contained" fullWidth sx={{ mt: 3, bgcolor: PRIMARY }} onClick={handleSubmit} disabled={loading}>Save Log Entry</Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Fuellogs;